import {
  FeatureName,
  Flow,
  FlowAsset,
  FlowStatus,
  Project,
  UserInputDub,
  UserInputReformatting,
  UserInputCaptions,
  UserInputUpscale,
} from '@contentlab/types/project';
import {
  ProjectFlow,
  ProjectProps,
  ThumbnailData,
} from '@contentlab/types/projectProps';
import translate from '@/utils/translate';

// Feature-based processing time configuration
// Each feature has a setup time (fixed overhead) and a multiplier for video duration
interface ProcessingTimeConfig {
  setupTimeMillis: number; // Fixed setup/initialization time
  durationMultiplier: number; // Multiplier for video duration
}

const PROCESSING_CONFIG: Record<FeatureName, ProcessingTimeConfig> = {
  [FeatureName.AI_DUBBING]: {
    setupTimeMillis: 5 * 60 * 1000, // 3 minutes setup
    durationMultiplier: 3,
  },
  [FeatureName.REPHRASING]: {
    setupTimeMillis: 3 * 60 * 1000, // 3 minutes setup
    durationMultiplier: 3,
  },
  [FeatureName.REFORMATTING]: {
    setupTimeMillis: 6 * 60 * 1000, // 6 minutes setup
    durationMultiplier: 14,
  },
  [FeatureName.CAPTIONS]: {
    setupTimeMillis: 4 * 60 * 1000, // 4 minutes setup
    durationMultiplier: 12,
  },
  [FeatureName.AI_AVATAR]: {
    setupTimeMillis: 5 * 60 * 1000, // 5 minutes setup
    durationMultiplier: 8,
  },
  [FeatureName.UPSCALE]: {
    setupTimeMillis: 1.5 * 60 * 1000, // 1.5 minutes setup
    durationMultiplier: 10,
  },
};

// Helper function to map flow_id to a deterministic random number between 1 and 1.2
function getFlowRandomMultiplier(flowId: string): number {
  // Simple hash function to convert string to number (avoiding bitwise operators)
  let hash = 0;
  for (let i = 0; i < flowId.length; i += 1) {
    const char = flowId.charCodeAt(i);
    hash = hash * 32 - hash + char;
    hash = Math.floor(hash % 2147483647); // Keep within 32-bit range
  }

  // Convert hash to positive number and normalize to 0-1 range
  const normalized = Math.abs(hash) / 2147483647; // Max 32-bit integer

  // Scale to range 1 - 1.2
  return 1 + normalized * 0.2;
}

// Helper to get the best thumbnail for a flow
function getThumbnailFromFlowAsset(flow: FlowAsset) {
  // Prefer outputAsset thumbnail, fallback to inputAsset
  const isOutputThumbnailAvailable =
    !!flow.flowAssetUrls.outputAssetThumbnailUrl;
  const assetVideo =
    flow.flowAssetUrls.outputAssetUrl || flow.flowAssetUrls.inputAssetUrl;
  const assetThumbnail =
    flow.flowAssetUrls.outputAssetThumbnailUrl ||
    flow.flowAssetUrls.inputAssetThumbnailUrl;
  const asset = isOutputThumbnailAvailable
    ? flow.flow.outputAsset
    : flow.flow.inputAsset;

  return asset
    ? {
        id: `${flow.flow.id}-${asset?.id}`,
        videoUrl: assetVideo,
        thumbnailUrl: assetThumbnail,
      }
    : null;
}

// Helper to calculate estimated completion time for a flow
function getEstimatedCompletionTime(
  flow: Flow,
  feature: FeatureName
): string | null {
  if ([FlowStatus.READY, FlowStatus.FAILED].includes(flow.status)) {
    // If the flow is in a terminal state, return null
    return null;
  }
  const createdAt = new Date(flow.createdAt).getTime();
  const durationMillis = flow.inputAsset?.metadata?.durationMillis || 0;

  // Get processing configuration for the feature
  const config = PROCESSING_CONFIG[feature] || {
    setupTimeMillis: 60 * 1000, // Default 1 minute setup
    durationMultiplier: 1, // Default 1x multiplier
  };

  const randomMultiplier = getFlowRandomMultiplier(flow.id);

  // Calculate estimated duration with different random effects:
  // - Setup time gets full random multiplier (varies 110%-150%)
  // - Video processing gets dampened random effect (varies 102%-110%)
  const setupTime = config.setupTimeMillis * randomMultiplier;
  const videoProcessingRandomness = 1 + (randomMultiplier - 1) * 0.2; // Dampen to 20% of original variance
  const processingTime =
    durationMillis * config.durationMultiplier * videoProcessingRandomness;

  const estimatedDuration = setupTime + processingTime;
  const estimated = createdAt + estimatedDuration;
  return Number.isNaN(estimated) ? null : new Date(estimated).toISOString();
}

// Helper to calculate progress percentage for a flow
function getProgressPercentage(
  flow: Flow,
  feature: FeatureName
): number | null {
  const createdAt = new Date(flow.createdAt).getTime();
  const estimatedCompletion = getEstimatedCompletionTime(flow, feature);
  if (!estimatedCompletion) {
    return null;
  }
  const estimated = new Date(estimatedCompletion).getTime();
  const now = Date.now();
  if (now >= estimated) {
    return 100;
  }
  if (now <= createdAt) {
    return 0;
  }
  return Math.min(
    100,
    Math.round(((now - createdAt) / (estimated - createdAt)) * 100)
  );
}

// Helper to get unique userInputs from flows
function getUniqueUserInputs(userInputs: string[]): string[] {
  return Array.from(
    new Set(userInputs.map((userInput) => userInput).filter(Boolean))
  );
}

// Helper to get language code from user input or flow metadata
function languageCodeFromFlow(
  userInput: UserInputDub | UserInputCaptions,
  flow: Flow
): string {
  return (
    userInput.language?.code?.toUpperCase() ||
    flow.outputAsset?.metadata?.language?.substring(0, 2)?.toUpperCase() ||
    ''
  );
}

/**
 * Processes user inputs from flows based on the feature type.
 * fallback to output asset metadata if user input is not available.
 * @param feature
 * @param flows
 */
function processFlowsUserInputs(feature: FeatureName, flows: Flow[]): string[] {
  if (feature === FeatureName.AI_DUBBING) {
    return flows.map((flow) =>
      languageCodeFromFlow(flow.userInput as UserInputDub, flow)
    );
  }
  if (feature === FeatureName.REFORMATTING) {
    return flows.map(
      (flow) =>
        (flow.userInput as UserInputReformatting)?.aspectRatio ||
        flow.outputAsset?.metadata?.aspectRatio ||
        ''
    );
  }
  if (feature === FeatureName.CAPTIONS) {
    return flows.map((flow) =>
      languageCodeFromFlow(flow.userInput as UserInputCaptions, flow)
    );
  }
  if (feature === FeatureName.UPSCALE) {
    return flows.map((flow) => {
      const userInputResolution = (flow.userInput as UserInputUpscale)
        ?.outputResolution;
      return userInputResolution ? `${userInputResolution}p` : '';
    });
  }
  return [];
}

function mapProjectToProjectProps(project: Project): ProjectProps {
  const featureName = translate(project.feature);

  // Map flows to ProjectFlow with progress and estimatedCompletionTime
  const projectFlows = project.flows.map((flow) => ({
    ...flow.flow,
    inputAsset: {
      ...flow.flow.inputAsset,
      url: flow.flowAssetUrls.inputAssetUrl,
      thumbnailUrl: flow.flowAssetUrls.inputAssetThumbnailUrl,
    },
    outputAsset: flow.flow.outputAsset
      ? {
          ...flow.flow.outputAsset,
          url: flow.flowAssetUrls.outputAssetUrl,
          thumbnailUrl: flow.flowAssetUrls.outputAssetThumbnailUrl,
        }
      : null,
    progressPercentage: getProgressPercentage(flow.flow, project.feature),
    estimatedCompletionTime: getEstimatedCompletionTime(
      flow.flow,
      project.feature
    ),
  }));

  // Thumbnails: take from flows, prefer outputAsset, fallback to inputAsset
  const thumbnails = (
    project.flows
      .map(getThumbnailFromFlowAsset)
      .filter(Boolean) as ThumbnailData[]
  ).slice(0, 4);

  // Unique userInputs
  const userInputs = getUniqueUserInputs(
    processFlowsUserInputs(
      project.feature,
      projectFlows.map((flow) => flow)
    )
  );

  // Project-level estimatedCompletionTime and progressPercentage: take from the flow with the furthest estimatedCompletionTime
  const furthestFlow = projectFlows.reduce(
    (acc: ProjectFlow | null, flow: ProjectFlow) => {
      if (!acc) {
        return flow;
      }
      const flowEstimate = flow.estimatedCompletionTime
        ? new Date(flow.estimatedCompletionTime).getTime()
        : 0;

      const accEstimate = acc.estimatedCompletionTime
        ? new Date(acc.estimatedCompletionTime).getTime()
        : 0;
      if (flowEstimate > accEstimate) {
        return flow;
      }
      return acc;
    },
    null
  );

  return {
    id: project.id,
    name: project.name,
    state: project.state,
    status: project.status,
    progressPercentage: furthestFlow?.progressPercentage ?? null,
    estimatedCompletionTime: furthestFlow?.estimatedCompletionTime ?? null,
    featureName,
    feature: project.feature,
    flows: projectFlows,
    thumbnails,
    userInputs,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
  };
}

export default mapProjectToProjectProps;
